import { useEffect, useMemo, useState } from "react";
import { http, fmtEUR, VAT_RATES, vatLabel } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { confirmDialog, promptDialog } from "@/lib/confirmDialog";
import StocktakeDialog from "@/components/StocktakeDialog";
import {
  Plus,
  Pencil,
  Trash2,
  Barcode,
  EyeOff,
  Eye,
  TagIcon,
  Ban,
  PauseCircle,
  Copy,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Star,
  ClipboardList,
  QrCode,
} from "lucide-react";

const empty = {
  code: "",
  description: "",
  barcode1: "",
  barcode2: "",
  barcode3: "",
  barcode1_qty: 1,
  barcode2_qty: 1,
  barcode3_qty: 1,
  price: 0,
  vat_rate: 3,
  department: null,
  category_id: null,
  color: null, // null = inherit from category
  is_active: true,
  is_hidden: false,
  sale_paused: false,
  no_discount: false,
  allow_price_change: true,
  show_in_qrmenu: false,
  qrmenu_comment: "",
  qrmenu_extra_group_ids: [],
  is_favorite: false,
  is_weighable: false,
  allow_comment: false,
  auto_open_comment: false,
  comment_group_ids: [],
  print_enabled: true,
  printer_override: false,
  printer_ids: [],
  stock_qty: 0,
  track_stock: false,
  sort_order: 0,
  // Feb 2026: per-channel pricing (RETAIL / RESTAURANT / DELIVERY).
  // Shared mode is the default — an existing item with a single
  // price/department automatically applies to all channels.
  use_shared_pricing: true,
  enabled_retail: true,
  enabled_restaurant: true,
  enabled_delivery: true,
  price_restaurant: null,
  price_delivery: null,
  department_restaurant: null,
  department_delivery: null,
  vat_rate_restaurant: null,
  vat_rate_delivery: null,
};

export default function Catalog() {
  // Ιούν 2026: module «Απόθεμα» ανά κατάστημα.
  const [stockModuleOn, setStockModuleOn] = useState(true);
  useEffect(() => {
    http.get("/settings")
      .then((r) => setStockModuleOn(r.data?.modules_stock !== false))
      .catch(() => {});
  }, []);
  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [stocktakeOpen, setStocktakeOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [filterCat, setFilterCat] = useState("all");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState(null); // column key
  const [sortDir, setSortDir] = useState("asc"); // asc | desc
  const [printers, setPrinters] = useState([]);
  const [commentGroups, setCommentGroups] = useState([]);

  const load = async () => {
    const [p, d, c, pr, cg] = await Promise.all([
      http.get("/products"),
      http.get("/departments"),
      http.get("/categories"),
      http.get("/printers"),
      http.get("/comment-groups"),
    ]);
    setItems(p.data);
    setDepartments(d.data);
    setCategories(c.data);
    setPrinters((pr.data || []).filter((x) => x.active !== false && !x.is_reports));
    setCommentGroups((cg.data || []).filter((x) => x.is_active));
  };
  useEffect(() => {
    const t = setTimeout(load, 0);
    return () => clearTimeout(t);
  }, []);

  // when the department changes, auto-fill VAT
  const setDept = (number) => {
    const d = departments.find((x) => x.number === Number(number));
    setForm((f) => ({
      ...f,
      department: Number(number),
      vat_rate: d ? d.vat_number : f.vat_rate,
    }));
  };

  const startNew = async () => {
    setEditing(null);
    let nextCode = "";
    try {
      const r = await http.get("/products/next-code");
      nextCode = r.data?.code || "";
    } catch (_e) {
      /* ignore */
    }
    setForm({ ...empty, code: nextCode });
    setOpen(true);
  };

  const startEdit = (it) => {
    setEditing(it.id);
    setForm({ ...empty, ...it });
    setOpen(true);
  };

  const startCopy = async (it) => {
    setEditing(null);
    let nextCode = "";
    try {
      const r = await http.get("/products/next-code");
      nextCode = r.data?.code || "";
    } catch (_e) {
      /* ignore */
    }
    // Clone everything from source EXCEPT id, code (auto), barcodes
    const {
      id: _id,
      code: _code,
      barcode1: _b1,
      barcode2: _b2,
      barcode3: _b3,
      ...rest
    } = it;
    setForm({
      ...empty,
      ...rest,
      code: nextCode,
      barcode1: "",
      barcode2: "",
      barcode3: "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (!form.description?.trim()) {
        toast.error("Συμπληρώστε την περιγραφή");
        return;
      }
      if (!form.price || Number(form.price) <= 0) {
        toast.error("Η τιμή πρέπει να είναι μεγαλύτερη του 0");
        return;
      }
      if (!form.department) {
        toast.error("Επιλέξτε τμήμα ταμειακής");
        return;
      }
      if (!form.category_id) {
        toast.error("Επιλέξτε κατηγορία");
        return;
      }
      if (editing) await http.put(`/products/${editing}`, form);
      else await http.post("/products", form);
      toast.success(editing ? "Ενημερώθηκε" : "Προστέθηκε");
      setEditing(null);
      setForm(empty);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    }
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(empty);
    setOpen(false);
  };

  const del = async (id) => {
    if (!(await confirmDialog("Διαγραφή είδους;"))) return;
    await http.delete(`/products/${id}`);
    toast.success("Διαγράφηκε");
    load();
  };

  const catById = (id) => categories.find((c) => c.id === id);
  const catColor = (id) => catById(id)?.color || "#94A3B8";
  const effectiveColor = (it) => it.color || catColor(it.category_id);

  const onSort = (key) => {
    if (sortBy === key) {
      // toggle asc -> desc -> none
      if (sortDir === "asc") setSortDir("desc");
      else {
        setSortBy(null);
        setSortDir("asc");
      }
    } else {
      setSortBy(key);
      setSortDir("asc");
    }
  };

  const sortValue = (it, key) => {
    switch (key) {
      case "code":
        return it.code || "";
      case "description":
        return (it.description || "").toLocaleLowerCase("el-GR");
      case "barcodes":
        return it.barcode1 || it.barcode2 || it.barcode3 || "";
      case "price":
        return Number(it.price) || 0;
      case "vat_rate":
        return Number(it.vat_rate) || 0;
      case "department":
        return Number(it.department) || 0;
      case "category":
        return (catById(it.category_id)?.name || "").toLocaleLowerCase("el-GR");
      case "stock_qty":
        return Number(it.stock_qty) || 0;
      default:
        return "";
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const arr = items.filter((it) => {
      if (filterCat !== "all" && it.category_id !== filterCat) return false;
      if (
        q &&
        !(
          it.description.toLowerCase().includes(q) ||
          (it.code || "").toLowerCase().includes(q) ||
          (it.barcode1 || "").toLowerCase().includes(q) ||
          (it.barcode2 || "").toLowerCase().includes(q) ||
          (it.barcode3 || "").toLowerCase().includes(q)
        )
      )
        return false;
      return true;
    });
    if (sortBy) {
      const dir = sortDir === "asc" ? 1 : -1;
      arr.sort((a, b) => {
        const va = sortValue(a, sortBy);
        const vb = sortValue(b, sortBy);
        if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
        return String(va).localeCompare(String(vb), "el-GR") * dir;
      });
    }
    return arr;
  }, [items, filterCat, search, categories, sortBy, sortDir]);

  const renderSortHeader = (k, label) => {
    const active = sortBy === k;
    const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <button
        type="button"
        onClick={() => onSort(k)}
        data-testid={`sort-${k}`}
        className={`inline-flex items-center gap-1 select-none hover:text-blue-700 transition-colors ${
          active ? "text-blue-700 font-bold" : "text-slate-700"
        }`}
        title={active ? `Ταξινόμηση ${sortDir === "asc" ? "αύξουσα" : "φθίνουσα"}` : "Ταξινόμηση"}
      >
        <span>{label}</span>
        <Icon size={12} className={active ? "" : "opacity-40"} />
      </button>
    );
  };

  return (
    <div className="h-full flex flex-col">
      <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-grid">
        <div>
          <h2 className="font-heading text-2xl font-bold">Κατάλογος Ειδών</h2>
          <div className="text-xs text-slate-500">
            Διαχείριση προϊόντων, barcodes, τιμών και κατηγοριών
          </div>
        </div>
        <div className="flex items-center gap-2">
          {stockModuleOn && (
          <Button
            variant="outline"
            onClick={() => setStocktakeOpen(true)}
            data-testid="stocktake-btn"
            className="h-11 font-bold border-emerald-600 text-emerald-700 hover:bg-emerald-50"
          >
            <ClipboardList size={18} className="mr-1.5" /> Απογραφή
          </Button>
          )}
          <Button
            onClick={startNew}
            data-testid="add-product-btn"
            className="bg-blue-600 hover:bg-blue-700 h-11 font-bold"
          >
            <Plus size={18} className="mr-1.5" /> Νέο Είδος
          </Button>
        </div>
      </div>

      <div className="px-6 py-3 border-b border-slate-200 flex items-center gap-3 flex-wrap">
        <Input
          placeholder="Αναζήτηση (περιγραφή, κωδικός, barcode)..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
          data-testid="catalog-search"
        />
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="catalog-cat-filter">
          <button
            onClick={() => setFilterCat("all")}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              filterCat === "all"
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white border-slate-200 hover:bg-slate-50"
            }`}
            data-testid="catalog-cat-all"
          >
            Όλες
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => setFilterCat(c.id)}
              data-testid={`catalog-cat-${c.id}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors flex items-center gap-1.5 ${
                filterCat === c.id
                  ? "text-white border-transparent"
                  : "bg-white border-slate-200 hover:bg-slate-50"
              }`}
              style={filterCat === c.id ? { background: c.color || "#2563EB" } : {}}
            >
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: c.color || "#2563EB" }}
              />
              {c.name}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scroll-thin p-6">
        <div className="border rounded-lg overflow-hidden" data-testid="products-table">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead className="w-24">{renderSortHeader("code", "Κωδ.")}</TableHead>
                <TableHead>{renderSortHeader("description", "Περιγραφή")}</TableHead>
                <TableHead className="font-mono">{renderSortHeader("barcodes", "Barcodes")}</TableHead>
                <TableHead>{renderSortHeader("price", "Τιμή")}</TableHead>
                <TableHead>{renderSortHeader("vat_rate", "ΦΠΑ")}</TableHead>
                <TableHead>{renderSortHeader("department", "Τμήμα")}</TableHead>
                <TableHead>{renderSortHeader("category", "Κατηγορία")}</TableHead>
                <TableHead className="w-16">{renderSortHeader("stock_qty", "Απόθ.")}</TableHead>
                <TableHead className="w-20">Flags</TableHead>
                <TableHead className="w-32"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((it) => (
                <TableRow key={it.id} className="hover:bg-slate-50">
                  <TableCell>
                    <span
                      className="w-5 h-5 rounded-full inline-block ring-2 ring-white shadow"
                      style={{ background: effectiveColor(it) }}
                    />
                  </TableCell>
                  <TableCell className="font-mono text-xs font-bold">{it.code}</TableCell>
                  <TableCell className="font-medium">{it.description}</TableCell>
                  <TableCell className="text-[11px] font-mono space-y-0.5">
                    {it.barcode1 && <div>{it.barcode1}</div>}
                    {it.barcode2 && <div className="text-slate-500">{it.barcode2}</div>}
                    {it.barcode3 && <div className="text-slate-500">{it.barcode3}</div>}
                    {!it.barcode1 && !it.barcode2 && !it.barcode3 && (
                      <span className="text-slate-300">—</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono tabular">{fmtEUR(it.price)}</TableCell>
                  <TableCell className="text-xs">{vatLabel(it.vat_rate)}</TableCell>
                  <TableCell className="text-xs">#{it.department}</TableCell>
                  <TableCell className="text-xs">
                    {catById(it.category_id)?.name || "—"}
                  </TableCell>
                  <TableCell className="text-xs font-mono tabular">{it.stock_qty || 0}</TableCell>
                  <TableCell>
                    <div className="flex gap-1 items-center">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const next = { ...it, is_favorite: !it.is_favorite };
                            await http.put(`/products/${it.id}`, next);
                            load();
                          } catch {
                            toast.error("Αποτυχία αλλαγής");
                          }
                        }}
                        title={it.is_favorite ? "Αφαίρεση από αγαπημένα" : "Πρόσθεση στα αγαπημένα"}
                        className={`p-0.5 rounded hover:bg-amber-50 ${
                          it.is_favorite ? "text-amber-500" : "text-slate-300"
                        }`}
                        data-testid={`fav-${it.id}`}
                      >
                        <Star size={16} fill={it.is_favorite ? "currentColor" : "none"} />
                      </button>
                      {it.is_hidden && (
                        <span title="Αόρατο από κατηγορίες" className="text-amber-600">
                          <EyeOff size={14} />
                        </span>
                      )}
                      {it.sale_paused && (
                        <span title="Παύση πώλησης" className="text-red-600">
                          <PauseCircle size={14} />
                        </span>
                      )}
                      {it.no_discount && (
                        <span title="Χωρίς έκπτωση" className="text-red-600">
                          <Ban size={14} />
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => startEdit(it)}
                        data-testid={`edit-${it.id}`}
                        title="Διόρθωση"
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => startCopy(it)}
                        className="text-blue-600"
                        data-testid={`copy-${it.id}`}
                        title="Αντιγραφή (νέος κωδικός, χωρίς barcodes)"
                      >
                        <Copy size={14} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => del(it.id)}
                        className="text-red-600"
                        data-testid={`del-${it.id}`}
                        title="Διαγραφή"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} className="text-center text-slate-400 py-8">
                    Κανένα είδος.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) {
            cancelEdit();
            return;
          }
          setOpen(o);
        }}
      >
        <DialogContent
          className="max-w-none w-screen h-[100dvh] sm:rounded-none p-0 gap-0 flex flex-col bg-slate-50"
          hideClose
          data-testid="product-dialog"
        >
          {/* Sticky header */}
          <DialogHeader className="px-6 py-4 border-b-2 border-slate-200 bg-white shrink-0">
            <DialogTitle className="text-xl font-bold">
              {editing ? "Επεξεργασία Είδους" : "Νέο Είδος"}
            </DialogTitle>
          </DialogHeader>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="grid grid-cols-2 gap-3 max-w-5xl mx-auto">
            <div className="grid gap-1">
              <Label>Κωδικός Είδους (5-ψήφιος)</Label>
              <Input
                value={form.code || ""}
                onChange={(e) =>
                  setForm({ ...form, code: e.target.value.replace(/\D/g, "").slice(0, 5) })
                }
                placeholder="00000"
                className="font-mono"
                data-testid="prod-code"
              />
            </div>
            <div className="col-span-1 grid gap-1">
              <Label>Περιγραφή</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="prod-desc"
              />
            </div>

            {/* Barcodes */}
            <div className="col-span-2 border border-slate-200 rounded-md p-3 bg-slate-50">
              <div className="text-xs font-bold text-slate-700 mb-2 flex items-center gap-1.5">
                <Barcode size={14} />
                Barcodes (έως 3 διαφορετικά) — ο αριθμός δίπλα = ποσότητα ανά σκανάρισμα (π.χ. εξάδα = 6)
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="flex items-center gap-1">
                    <Input
                      placeholder={`Barcode ${n}`}
                      value={form[`barcode${n}`] || ""}
                      onChange={(e) => setForm({ ...form, [`barcode${n}`]: e.target.value })}
                      className="font-mono flex-1"
                      data-testid={`prod-barcode${n}`}
                    />
                    <span className="text-xs text-slate-400 font-bold">×</span>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={form[`barcode${n}_qty`] ?? 1}
                      onChange={(e) =>
                        setForm({ ...form, [`barcode${n}_qty`]: parseFloat(e.target.value) || 1 })
                      }
                      className="font-mono w-16 text-center"
                      data-testid={`prod-barcode${n}-qty`}
                      title="Ποσότητα ανά σκανάρισμα"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-1">
              <Label>Τιμή €</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={form.price}
                onChange={(e) =>
                  setForm({ ...form, price: parseFloat(e.target.value) || 0 })
                }
                data-testid="prod-price"
              />
            </div>
            <div className="grid gap-1">
              <Label>Απόθεμα</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="0.01"
                  value={form.stock_qty ?? 0}
                  onChange={(e) =>
                    setForm({ ...form, stock_qty: parseFloat(e.target.value) || 0 })
                  }
                  data-testid="prod-stock"
                  className="flex-1"
                />
                {stockModuleOn && (
                <label
                  className="flex items-center gap-1.5 text-xs font-bold whitespace-nowrap cursor-pointer select-none"
                  title="Έλεγχος αποθέματος: μειώνεται αυτόματα στις πωλήσεις και εμφανίζεται badge στο κουμπί του είδους"
                >
                  <input
                    type="checkbox"
                    checked={!!form.track_stock}
                    onChange={(e) => setForm({ ...form, track_stock: e.target.checked })}
                    data-testid="prod-track-stock"
                    className="w-4 h-4 accent-emerald-600"
                  />
                  Έλεγχος
                </label>
                )}
              </div>
            </div>

            <div className="grid gap-1">
              <Label>
                Τμήμα Ταμειακής <span className="text-red-600">*</span>
              </Label>
              <Select
                value={form.department ? String(form.department) : ""}
                onValueChange={(v) => setDept(v)}
              >
                <SelectTrigger data-testid="prod-dept">
                  <SelectValue placeholder="— Επιλέξτε —" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((d) => (
                    <SelectItem key={d.number} value={String(d.number)}>
                      #{d.number} – {d.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1">
              <Label className="flex items-center gap-1.5">
                ΦΠΑ
                <span className="text-[10px] text-slate-500 font-normal">
                  (από τμήμα)
                </span>
              </Label>
              <div
                className="h-10 rounded-md border-2 border-slate-200 bg-slate-100 px-3 flex items-center text-sm font-medium text-slate-700"
                data-testid="prod-vat"
              >
                {form.department
                  ? vatLabel(form.vat_rate)
                  : <span className="text-slate-400">—</span>}
              </div>
            </div>

            {/* Feb 2026 — Per-Channel Pricing (RETAIL / RESTAURANT / DELIVERY).
                Master «κοινή τιμή» checkbox at the top; when disabled the
                three rows open up with their own price + department + on/off
                per channel. */}
            <div
              className="col-span-2 border-2 border-blue-200 rounded-md p-3 bg-blue-50/40"
              data-testid="prod-channels-section"
            >
              <label className="flex items-center gap-2 mb-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="w-4 h-4"
                  checked={!!form.use_shared_pricing}
                  onChange={(e) =>
                    setForm({ ...form, use_shared_pricing: e.target.checked })
                  }
                  data-testid="prod-use-shared"
                />
                <span className="text-sm font-bold text-blue-900">
                  Χρήση κοινής τιμής / τμήματος για όλα τα κανάλια
                </span>
                <span className="text-[11px] text-slate-500">
                  (RETAIL · RESTAURANT · DELIVERY)
                </span>
              </label>
              {!form.use_shared_pricing && (
                <div className="space-y-2">
                  {[
                    { key: "retail", label: "RETAIL", color: "emerald" },
                    { key: "restaurant", label: "RESTAURANT", color: "amber" },
                    { key: "delivery", label: "DELIVERY", color: "indigo" },
                  ].map((ch) => {
                    const enabled = form[`enabled_${ch.key}`];
                    const priceKey = ch.key === "retail" ? "price" : `price_${ch.key}`;
                    const deptKey = ch.key === "retail" ? "department" : `department_${ch.key}`;
                    const priceVal = form[priceKey];
                    const deptVal = form[deptKey];
                    return (
                      <div
                        key={ch.key}
                        className={`grid grid-cols-[auto_100px_1fr_1fr] gap-2 items-center p-2 rounded-md border ${
                          enabled ? "bg-white border-slate-200" : "bg-slate-100 border-slate-200 opacity-60"
                        }`}
                        data-testid={`prod-channel-row-${ch.key}`}
                      >
                        <input
                          type="checkbox"
                          className="w-4 h-4"
                          checked={!!enabled}
                          onChange={(e) =>
                            setForm({ ...form, [`enabled_${ch.key}`]: e.target.checked })
                          }
                          data-testid={`prod-channel-enable-${ch.key}`}
                        />
                        <div className={`text-xs font-bold uppercase text-${ch.color}-700`}>
                          {ch.label}
                        </div>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Τιμή €"
                          value={priceVal ?? ""}
                          disabled={!enabled}
                          onChange={(e) => {
                            const v = e.target.value === "" ? null : parseFloat(e.target.value) || 0;
                            setForm({ ...form, [priceKey]: v });
                          }}
                          data-testid={`prod-channel-price-${ch.key}`}
                          className="text-sm"
                        />
                        <Select
                          value={deptVal ? String(deptVal) : ""}
                          onValueChange={(v) => {
                            const num = Number(v);
                            const d = departments.find((x) => x.number === num);
                            const patch = { [deptKey]: num };
                            // Auto-fill VAT from selected department.
                            if (ch.key === "retail") {
                              patch.vat_rate = d ? d.vat_number : form.vat_rate;
                            } else {
                              patch[`vat_rate_${ch.key}`] = d ? d.vat_number : form[`vat_rate_${ch.key}`];
                            }
                            setForm({ ...form, ...patch });
                          }}
                          disabled={!enabled}
                        >
                          <SelectTrigger data-testid={`prod-channel-dept-${ch.key}`} className="text-sm">
                            <SelectValue placeholder="— Τμήμα —" />
                          </SelectTrigger>
                          <SelectContent>
                            {departments.map((d) => (
                              <SelectItem key={d.number} value={String(d.number)}>
                                #{d.number} – {d.description} ({d.vat_number}%)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                  <div className="text-[11px] text-slate-500 italic mt-1">
                    Αν αφαιρέσετε το checkmark ενός καναλιού, το είδος δεν θα εμφανίζεται ούτε θα πωλείται εκεί.
                    Η πρώτη γραμμή (RETAIL) χρησιμοποιεί τη βασική τιμή & τμήμα από πάνω.
                  </div>
                </div>
              )}
            </div>

            <div className="grid gap-1">
              <Label className="flex items-center gap-1.5">
                <TagIcon size={12} /> Κατηγορία <span className="text-red-600">*</span>
              </Label>
              <Select
                value={form.category_id || ""}
                onValueChange={(v) =>
                  setForm({
                    ...form,
                    category_id: v || null,
                  })
                }
              >
                <SelectTrigger data-testid="prod-category">
                  <SelectValue placeholder="— Επιλέξτε —" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1">
              <Label className="flex items-center gap-1.5">
                Χρώμα
                <span className="text-[10px] text-slate-500 font-normal">
                  (από κατηγορία αν κενό)
                </span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  value={form.color || catColor(form.category_id) || "#2563EB"}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="w-14 p-1"
                  data-testid="prod-color"
                />
                {form.color && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setForm({ ...form, color: null })}
                    data-testid="prod-color-clear"
                  >
                    Επαναφορά
                  </Button>
                )}
                <span
                  className="w-7 h-7 rounded-full ring-2 ring-slate-300"
                  style={{
                    background: form.color || catColor(form.category_id) || "#94A3B8",
                  }}
                />
              </div>
            </div>

            {/* Flags */}
            <div className="col-span-2 grid grid-cols-3 gap-3 pt-2 border-t border-slate-200">
              <label className="flex items-center gap-3 p-3 border-2 border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                <Switch
                  checked={!!form.is_hidden}
                  onCheckedChange={(v) => setForm({ ...form, is_hidden: v })}
                  data-testid="prod-hidden"
                />
                <div className="flex-1">
                  <div className="font-bold text-sm flex items-center gap-1.5">
                    <EyeOff size={14} /> ΑΟΡΑΤΟ
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Δεν φαίνεται στις κατηγορίες, αλλά πωλείται κανονικά με barcode/scan
                  </div>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 border-2 border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                <Switch
                  checked={!!form.sale_paused}
                  onCheckedChange={(v) => setForm({ ...form, sale_paused: v })}
                  data-testid="prod-paused"
                />
                <div className="flex-1">
                  <div className="font-bold text-sm flex items-center gap-1.5">
                    <PauseCircle size={14} /> ΠΑΥΣΗ ΠΩΛΗΣΗΣ
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Δεν επιτρέπεται καθόλου η πώληση — ούτε με scan/αναζήτηση
                  </div>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 border-2 border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                <Switch
                  checked={!!form.no_discount}
                  onCheckedChange={(v) => setForm({ ...form, no_discount: v })}
                  data-testid="prod-no-discount"
                />
                <div className="flex-1">
                  <div className="font-bold text-sm flex items-center gap-1.5">
                    <Ban size={14} /> ΟΧΙ ΕΚΠΤΩΣΗ
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Δεν επιτρέπεται έκπτωση στο είδος αυτό
                  </div>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 border-2 border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                <Switch
                  checked={form.allow_price_change !== false}
                  onCheckedChange={(v) => setForm({ ...form, allow_price_change: v })}
                  data-testid="prod-allow-price-change"
                />
                <div className="flex-1">
                  <div className="font-bold text-sm flex items-center gap-1.5">
                    <Pencil size={14} /> ΑΛΛΑΓΗ ΤΙΜΗΣ
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Επιτρέπεται αλλαγή τιμής στο ταμείο (default: ενεργό)
                  </div>
                </div>
              </label>
              <label className="flex flex-col gap-2 p-3 border-2 border-violet-200 bg-violet-50/40 rounded-lg cursor-pointer hover:bg-violet-50">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={!!form.show_in_qrmenu}
                    onCheckedChange={(v) => setForm({ ...form, show_in_qrmenu: v })}
                    data-testid="prod-qrmenu"
                  />
                  <div className="flex-1">
                    <div className="font-bold text-sm flex items-center gap-1.5 text-violet-800">
                      <QrCode size={14} /> QR MENU
                    </div>
                    <div className="text-[11px] text-slate-600">
                      Εμφανίζεται στον δημόσιο QR τιμοκατάλογο (η Παύση Πώλησης το κρύβει αυτόματα)
                    </div>
                  </div>
                </div>
                {form.show_in_qrmenu && (
                  <>
                    <Input
                      value={form.qrmenu_comment || ""}
                      onChange={(e) => setForm({ ...form, qrmenu_comment: e.target.value })}
                      onClick={(e) => e.preventDefault()}
                      placeholder="Σχόλιο QR Menu (π.χ. συστατικά, περιγραφή)"
                      data-testid="prod-qrmenu-comment"
                    />
                    {commentGroups.length > 0 && (
                      <div>
                        <div className="text-[11px] font-bold text-violet-800 mb-1">
                          Extra που εμφανίζονται στο QR Menu (π.χ. γεύσεις):
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {commentGroups.map((g) => {
                            const checked = (form.qrmenu_extra_group_ids || []).includes(g.id);
                            return (
                              <button
                                key={g.id}
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  const next = checked
                                    ? (form.qrmenu_extra_group_ids || []).filter((x) => x !== g.id)
                                    : [...(form.qrmenu_extra_group_ids || []), g.id];
                                  setForm({ ...form, qrmenu_extra_group_ids: next });
                                }}
                                data-testid={`prod-qrmenu-extra-${g.id}`}
                                className={`px-2.5 h-7 rounded-full text-xs font-bold border-2 transition-colors ${
                                  checked
                                    ? "bg-violet-600 border-violet-600 text-white"
                                    : "bg-white border-slate-200 text-slate-600"
                                }`}
                              >
                                {g.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </label>
              <label className="flex items-center gap-3 p-3 border-2 border-amber-200 bg-amber-50/40 rounded-lg cursor-pointer hover:bg-amber-50">
                <Switch
                  checked={!!form.is_favorite}
                  onCheckedChange={(v) => setForm({ ...form, is_favorite: v })}
                  data-testid="prod-favorite"
                />
                <div className="flex-1">
                  <div className="font-bold text-sm flex items-center gap-1.5 text-amber-800">
                    <Star size={14} fill="currentColor" /> ΑΓΑΠΗΜΕΝΟ
                  </div>
                  <div className="text-[11px] text-slate-600">
                    Εμφανίζεται στη σταθερή σειρά (max 6) στο κάτω μέρος του POS
                  </div>
                </div>
              </label>
              {/* Ζυγιζόμενο — δέχεται scale EAN-13 (πρόθεμα από Ρυθμίσεις › ΖΥΓΟΣ). */}
              <label className="flex items-center gap-3 p-3 border-2 border-teal-200 bg-teal-50/40 rounded-lg cursor-pointer hover:bg-teal-50">
                <Switch
                  checked={!!form.is_weighable}
                  onCheckedChange={(v) => setForm({ ...form, is_weighable: v })}
                  data-testid="prod-weighable"
                />
                <div className="flex-1">
                  <div className="font-bold text-sm flex items-center gap-1.5 text-teal-800">
                    <Barcode size={14} /> ΖΥΓΙΖΟΜΕΝΟ
                  </div>
                  <div className="text-[11px] text-slate-600">
                    Δέχεται scale EAN-13 (το barcode κωδικοποιεί βάρος ή αξία· ο κωδικός είδους είναι ψηφία 3-7)
                  </div>
                </div>
              </label>
            </div>

            {/* Comments + per-product printer routing */}
            <div className="col-span-2 grid grid-cols-2 gap-3 pt-2 border-t border-slate-200">
              <label className="flex items-center gap-3 p-3 border-2 border-blue-200 bg-blue-50/40 rounded-lg cursor-pointer hover:bg-blue-50">
                <Switch
                  checked={!!form.allow_comment}
                  onCheckedChange={(v) =>
                    setForm({ ...form, allow_comment: v, auto_open_comment: v ? form.auto_open_comment : false })
                  }
                  data-testid="prod-allow-comment"
                />
                <div className="flex-1">
                  <div className="font-bold text-sm flex items-center gap-1.5 text-blue-800">
                    ΕΠΙΤΡΕΠΕΤΑΙ ΣΧΟΛΙΟ
                  </div>
                  <div className="text-[11px] text-slate-600">
                    Πατώντας το είδος στο cart ανοίγει dialog για εισαγωγή σχολίου (π.χ. «Κρύος, με ζάχαρη»)
                  </div>
                </div>
              </label>
              <label className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer ${form.allow_comment ? "border-blue-200 bg-blue-50/40 hover:bg-blue-50" : "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed"}`}>
                <Switch
                  checked={!!form.auto_open_comment}
                  disabled={!form.allow_comment}
                  onCheckedChange={(v) => setForm({ ...form, auto_open_comment: v })}
                  data-testid="prod-auto-open-comment"
                />
                <div className="flex-1">
                  <div className="font-bold text-sm flex items-center gap-1.5 text-blue-800">
                    ΑΥΤΟΜΑΤΟ ΑΝΟΙΓΜΑ ΣΧΟΛΙΩΝ
                  </div>
                  <div className="text-[11px] text-slate-600">
                    Μόλις προστεθεί στην παραγγελία, ανοίγει αμέσως το dialog σχολίων
                  </div>
                </div>
              </label>
              {form.allow_comment && (
                <label className="col-span-2 flex items-start gap-3 p-3 border-2 border-blue-200 bg-blue-50/40 rounded-lg cursor-default">
                  <div className="flex-1">
                    <div className="font-bold text-sm text-blue-800 mb-1">
                      ΟΜΑΔΕΣ ΣΧΟΛΙΩΝ
                    </div>
                    <div className="text-[11px] text-slate-600 mb-2">
                      Διάλεξε μία ή περισσότερες ομάδες σχολίων από τις Ρυθμίσεις → Σχόλια. Όλα τα σχόλια αυτών των ομάδων θα είναι διαθέσιμα στον σερβιτόρο.
                    </div>
                    {commentGroups.length === 0 ? (
                      <div className="text-xs text-slate-500 italic">
                        Δεν υπάρχουν ομάδες σχολίων. Πήγαινε στις Ρυθμίσεις → Σχόλια.
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-1.5" data-testid="prod-comment-group-list">
                        {commentGroups.map((g) => {
                          const checked = (form.comment_group_ids || []).includes(g.id);
                          return (
                            <label
                              key={g.id}
                              className={`flex items-center gap-2 px-2 py-1.5 rounded border-2 text-xs cursor-pointer ${checked ? "border-blue-500 bg-white" : "border-slate-200 bg-white/60 hover:bg-white"}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const next = e.target.checked
                                    ? [...(form.comment_group_ids || []), g.id]
                                    : (form.comment_group_ids || []).filter((x) => x !== g.id);
                                  setForm({ ...form, comment_group_ids: next });
                                }}
                                data-testid={`prod-comment-group-${g.id}`}
                              />
                              <span className="font-bold">{g.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                    {(form.comment_group_ids || []).length > 1 && (
                      <div className="mt-3" data-testid="prod-comment-group-order">
                        <div className="text-[11px] font-bold text-blue-800 mb-1">
                          Σειρά εμφάνισης στο ταμείο (▲▼):
                        </div>
                        <div className="space-y-1">
                          {(form.comment_group_ids || []).map((gid, idx) => {
                            const g = commentGroups.find((x) => x.id === gid);
                            if (!g) return null;
                            const move = (dir) => {
                              const arr = [...form.comment_group_ids];
                              const j = idx + dir;
                              if (j < 0 || j >= arr.length) return;
                              [arr[idx], arr[j]] = [arr[j], arr[idx]];
                              setForm({ ...form, comment_group_ids: arr });
                            };
                            return (
                              <div
                                key={gid}
                                className="flex items-center gap-2 bg-white border-2 border-blue-200 rounded px-2 py-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[11px] font-bold grid place-items-center shrink-0">
                                  {idx + 1}
                                </span>
                                <span className="flex-1 text-xs font-bold truncate">{g.name}</span>
                                <button
                                  type="button"
                                  onClick={(e) => { e.preventDefault(); move(-1); }}
                                  disabled={idx === 0}
                                  className="p-1 text-blue-700 disabled:opacity-25"
                                  data-testid={`prod-cg-up-${gid}`}
                                >
                                  <ArrowUp size={14} />
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => { e.preventDefault(); move(1); }}
                                  disabled={idx === (form.comment_group_ids || []).length - 1}
                                  className="p-1 text-blue-700 disabled:opacity-25"
                                  data-testid={`prod-cg-down-${gid}`}
                                >
                                  <ArrowDown size={14} />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </label>
              )}
              <label className="col-span-2 flex items-start gap-3 p-3 border-2 border-emerald-200 bg-emerald-50/40 rounded-lg cursor-pointer hover:bg-emerald-50">
                <Switch
                  checked={!!form.printer_override}
                  onCheckedChange={(v) => setForm({ ...form, printer_override: v })}
                  data-testid="prod-printer-override"
                />
                <div className="flex-1">
                  <div className="font-bold text-sm text-emerald-800">
                    ΕΚΤΥΠΩΤΕΣ ΠΑΡΑΓΓΕΛΙΑΣ (override κατηγορίας)
                  </div>
                  <div className="text-[11px] text-slate-600 mb-2">
                    Default: κληρονομεί τους εκτυπωτές της κατηγορίας. Ενεργοποίησε για να ορίσεις custom λίστα μόνο γι&apos; αυτό το είδος.
                  </div>
                  {form.printer_override && (
                    <div className="grid grid-cols-2 gap-1.5 mt-1" data-testid="prod-printer-list">
                      {printers.length === 0 && (
                        <div className="col-span-2 text-xs text-slate-500 italic">
                          Δεν υπάρχουν δηλωμένοι εκτυπωτές παραγγελίας. Πήγαινε στις Ρυθμίσεις → Εκτυπωτές.
                        </div>
                      )}
                      {printers.map((pr) => {
                        const checked = (form.printer_ids || []).includes(pr.id);
                        return (
                          <label
                            key={pr.id}
                            className={`flex items-center gap-2 px-2 py-1.5 rounded border-2 text-xs cursor-pointer ${checked ? "border-emerald-500 bg-white" : "border-slate-200 bg-white/60 hover:bg-white"}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const next = e.target.checked
                                  ? [...(form.printer_ids || []), pr.id]
                                  : (form.printer_ids || []).filter((x) => x !== pr.id);
                                setForm({ ...form, printer_ids: next });
                              }}
                              data-testid={`prod-printer-${pr.id}`}
                            />
                            <span className="font-bold">{pr.name}</span>
                            <span className="text-slate-400">({pr.printer_type})</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </label>
            </div>
          </div>
          </div>{/* /scrollable body */}

          {/* Sticky footer */}
          <DialogFooter className="px-6 py-4 pr-52 border-t-2 border-slate-200 bg-white shrink-0 flex flex-row justify-end gap-3 sm:gap-3">
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={cancelEdit}
              className="h-12 font-bold border-2 px-6"
              data-testid="cancel-product-btn"
            >
              Ακύρωση
            </Button>
            <Button
              size="lg"
              onClick={save}
              className="bg-blue-600 hover:bg-blue-700 h-12 font-bold px-8"
              data-testid="save-product-btn"
            >
              {editing ? "Καταχώρηση" : "Καταχώρηση"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StocktakeDialog open={stocktakeOpen} onOpenChange={setStocktakeOpen} onSaved={load} />
    </div>
  );
}
